import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group';
import Spinner from '@cloudscape-design/components/spinner';
import Button from '@cloudscape-design/components/button';
import { useAppSelector, useAppDispatch } from 'src/store/hooks';
import { fetchPayment, clearPayment } from 'src/store/slices/payment/paymentSlice';
import { addNotification, clearNotifications } from 'src/store/slices/notifications/notificationSlice';
import { PaymentHeader } from './header/PaymentHeader';
import { PaymentErrorBanner } from './errors/PaymentErrorBanner';
import { PaymentTimeline } from './timeline/PaymentTimeline';
import { TransmissionSection } from './transmissions/TransmissionSection';

export const PaymentDetailPage = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id') ?? '';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: payment, loading, error } = useAppSelector(state => state.payment);

  useEffect(() => {
    if (id) {
      dispatch(clearNotifications());
      dispatch(clearPayment());
      const promise = dispatch(fetchPayment(id));
      return () => { promise.abort(); dispatch(clearPayment()); };
    }
    return () => { dispatch(clearPayment()); };
  }, [id, dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(clearNotifications());
      dispatch(addNotification({ type: 'error', header: 'Failed to load payment', content: error }));
    }
  }, [error, dispatch]);

  const handleRefresh = () => {
    if (id) {
      dispatch(clearPayment());
      dispatch(fetchPayment(id));
    }
  };

  return (
    <Box padding={{ horizontal: 'l', top: 'l', bottom: 'xl' }}>
      <SpaceBetween size="l">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <BreadcrumbGroup
            items={[
              { text: 'Search', href: '/' },
              { text: `Payment: ${id}`, href: '#' },
            ]}
            onFollow={(e) => {
              e.preventDefault();
              navigate(e.detail.href);
            }}
          />
          <Button iconName="refresh" onClick={handleRefresh} loading={loading}>
            Refresh
          </Button>
        </div>

        {loading && !payment && (
          <Box textAlign="center" padding={{ top: 'xxxl' }}>
            <SpaceBetween size="s" alignItems="center">
              <Spinner size="large" />
              <Box variant="p" color="text-body-secondary">
                Loading payment {id}...
              </Box>
            </SpaceBetween>
          </Box>
        )}

        {payment && (
          <SpaceBetween size="l">
            <PaymentHeader />
            <PaymentErrorBanner />
            <PaymentTimeline />
            <TransmissionSection />
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Box>
  );
};
